import { avatarImg, closeIcon, dividerStraightIcon, heartIcon, logoImg, navigationIcon, triangleDownIcon } from '../../utils/images';
import './Header.scss';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { client } from '../../utils/fetch';
import { supabase } from '../../utils/supabaseClient';

export const Header: React.FC = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isDeleteAccountOpen, setIsDeleteAccountOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [accountActionError, setAccountActionError] = useState('');
  const [accountActionSuccess, setAccountActionSuccess] = useState('');
  const [isAccountActionLoading, setIsAccountActionLoading] = useState(false);
  const navigate = useNavigate();
  const { signOut, signOutEverywhere, session, role, loading } = useAuth();
  const accountRef = useRef<HTMLDivElement>(null);
  const showStoreHeadline = !loading && role !== null && role !== 'personal';
  const accountEmail = session?.user?.email ?? '';

  const resetAccountActionState = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setDeleteConfirmation('');
    setAccountActionError('');
    setAccountActionSuccess('');
    setIsAccountActionLoading(false);
  };

  const openChangePasswordModal = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    resetAccountActionState();
    setIsAccountMenuOpen(false);
    setIsChangePasswordOpen(true);
  };

  const openDeleteAccountModal = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    resetAccountActionState();
    setIsAccountMenuOpen(false);
    setIsDeleteAccountOpen(true);
  };

  const closeAccountModal = () => {
    setIsChangePasswordOpen(false);
    setIsDeleteAccountOpen(false);
    resetAccountActionState();
  };

  const handleSignOut = async () => {
    setIsAccountMenuOpen(false);
    await signOut();
    navigate('/');
  };

  const handleSignOutEverywhere = async () => {
    setIsAccountMenuOpen(false);
    await signOutEverywhere();
    navigate('/');
  };

  let storeNameT = localStorage.getItem('storeName');

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsDrawerOpen(false);
  };

  const handleChangePassword = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAccountActionError('');
    setAccountActionSuccess('');

    if (!accountEmail) {
      setAccountActionError('Could not find your account email. Please sign in again.');
      return;
    }

    if (newPassword.length < 6) {
      setAccountActionError('New password must be at least 6 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setAccountActionError('New passwords do not match.');
      return;
    }

    setIsAccountActionLoading(true);
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: accountEmail,
      password: currentPassword,
    });

    if (signInError) {
      setIsAccountActionLoading(false);
      setAccountActionError('Current password is incorrect.');
      return;
    }

    const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
    setIsAccountActionLoading(false);

    if (updateError) {
      setAccountActionError(updateError.message || 'Could not change password.');
      return;
    }

    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setAccountActionSuccess('Password changed successfully.');
  };

  const handleDeleteAccount = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAccountActionError('');

    if (deleteConfirmation !== accountEmail) {
      setAccountActionError('Type your account email exactly to confirm deletion.');
      return;
    }

    setIsAccountActionLoading(true);
    try {
      await client.delete<{ deleted: boolean }>('users/me', null);
      await supabase.auth.signOut({ scope: 'local' });
      closeAccountModal();
      navigate('/');
    } catch (error) {
      setIsAccountActionLoading(false);
      setAccountActionError(error instanceof Error ? error.message : 'Could not delete account.');
    }
  };

  return (
    <>
    <header className="header">
      <div className="header__leading-element">
        <img 
          className="header__navigation" 
          src={navigationIcon} 
          alt='navigation icon'
          onClick={toggleDrawer}
        />
        <div className="header__logo">
          <img src={logoImg} alt="Logo" className="header__logo-image" />
          <span className="header__brandname">Dovery</span>
        </div>
      </div>
      {showStoreHeadline && (
        <div className="header__headline">
          <span className="header__divider">/</span>
            <img className="header__emoji" src={heartIcon} alt='heart icon' />
            <h1 className="header__title">{storeNameT}</h1>
        </div>
      )}
      <div className="header__container-right">
        {/* <div className="header__search">
          <input type="text" className="header__search-input" placeholder="Search..." />
        </div> */}
        <div className="header__tabs">
        </div>
        <div className="header__settings">
          <img className="header__divider" src={dividerStraightIcon} alt=''/>
          <div className="header__weather"></div>
          <hr className="header__divider" />
          {/* <div className="header__notify-button">
            <img className="header__notify-button-img" src={notificationIcon} alt="notification" />
          </div> */}
          <div
            className="header__account"
            ref={accountRef}
            onClick={() => setIsAccountMenuOpen((prev) => !prev)}
            style={{ cursor: 'pointer', position: 'relative' }}
          >
            <img src={avatarImg} alt="User Avatar" className="header__account-avatar" />
            <img src={triangleDownIcon} alt="" className="header__account-dropdown" />

            {isAccountMenuOpen && (
              <div className="header__account-menu" onClick={(event) => event.stopPropagation()}>
                {session?.user?.email && (
                  <div className="header__account-menu-email">{session.user.email}</div>
                )}
                <button className="header__account-menu-action" onClick={openChangePasswordModal}>
                  Change Password
                </button>
                <button className="header__account-menu-action header__account-menu-action--danger" onClick={openDeleteAccountModal}>
                  Delete Account
                </button>
                <button className="header__account-menu-signout" onClick={handleSignOut}>
                  Sign Out
                </button>
                <button className="header__account-menu-signout header__account-menu-signout--everywhere" onClick={handleSignOutEverywhere}>
                  Sign Out Everywhere
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>


    <div className={`navigation-drawer ${isDrawerOpen ? 'open' : ''}`}>
      <div className="navigation-drawer__headline">
        <img 
          className="navigation-drawer__headline--close" 
          src={closeIcon} 
          alt="close icon" 
          onClick={toggleDrawer}
        />
      </div>

        <div className="navigation-drawer__top">
          <div className="navigation-drawer__menu" onClick={() => handleNavigation('/my-displays')}>
            <div className="navigation-drawer__wrapper">
              <div className="navigation-drawer__label">
                <p className="navigation-drawer__label--text">My Displays</p>
              </div>
            </div>
          </div>
          {!loading && role === 'personal' && (
            <div className="navigation-drawer__menu" onClick={() => handleNavigation('/my-media')}>
              <div className="navigation-drawer__wrapper">
                <div className="navigation-drawer__label">
                  <p className="navigation-drawer__label--text">My Media</p>
                </div>
              </div>
            </div>
          )}
          {showStoreHeadline && (
            <>
              <div className="navigation-drawer__menu" onClick={() => handleNavigation('/stores')}>
                <div className="navigation-drawer__wrapper">
                  <div className="navigation-drawer__label">
                    <p className="navigation-drawer__label--text">Stores</p>
                  </div>
                </div>
              </div>
              <div className="navigation-drawer__menu" onClick={() => handleNavigation('/displays')}>
                <div className="navigation-drawer__wrapper">
                  <div className="navigation-drawer__label">
                    <p className="navigation-drawer__label--text">Display</p>
                  </div>
                </div>
              </div>
              <div className="navigation-drawer__menu" onClick={() => handleNavigation('/scenario')}>
                <div className="navigation-drawer__wrapper">
                  <div className="navigation-drawer__label">
                    <p className="navigation-drawer__label--text">Scenario</p>
                  </div>
                </div>
              </div>
              <div className="navigation-drawer__menu" onClick={() => handleNavigation('/device-map')}>
                <div className="navigation-drawer__wrapper">
                  <div className="navigation-drawer__label">
                    <p className="navigation-drawer__label--text">Device Map</p>
                  </div>
                </div>
              </div>
              <div className="navigation-drawer__menu" onClick={() => handleNavigation('/products')}>
                <div className="navigation-drawer__wrapper">
                  <div className="navigation-drawer__label">
                    <p className="navigation-drawer__label--text">Products</p>
                  </div>
                </div>
              </div>
              <div className="navigation-drawer__menu" onClick={() => handleNavigation('/media-library')}>
                <div className="navigation-drawer__wrapper">
                  <div className="navigation-drawer__label">
                    <p className="navigation-drawer__label--text">Media Library</p>
                  </div>
                </div>
              </div>
              <div className="navigation-drawer__menu" onClick={() => handleNavigation('/tags')}>
                <div className="navigation-drawer__wrapper">
                  <div className="navigation-drawer__label">
                    <p className="navigation-drawer__label--text">Tags</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
    </div>
    {(isChangePasswordOpen || isDeleteAccountOpen) && (
      <div className="account-modal" role="dialog" aria-modal="true" onClick={closeAccountModal}>
        <div className="account-modal__panel" onClick={(event) => event.stopPropagation()}>
          <div className="account-modal__header">
            <h2 className="account-modal__title">
              {isChangePasswordOpen ? 'Change Password' : 'Delete Account'}
            </h2>
            <button className="account-modal__close" type="button" onClick={closeAccountModal} aria-label="Close">
              x
            </button>
          </div>

          {isChangePasswordOpen && (
            <form className="account-modal__form" onSubmit={handleChangePassword}>
              <label className="account-modal__field">
                <span>Current password</span>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(event) => setCurrentPassword(event.target.value)}
                  autoComplete="current-password"
                  required
                />
              </label>
              <label className="account-modal__field">
                <span>New password</span>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  autoComplete="new-password"
                  required
                />
              </label>
              <label className="account-modal__field">
                <span>Confirm new password</span>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  autoComplete="new-password"
                  required
                />
              </label>
              <button
                className="account-modal__link"
                type="button"
                onClick={() => {
                  closeAccountModal();
                  navigate('/forgot-password');
                }}
              >
                Forgot password?
              </button>
              {accountActionError && <p className="account-modal__message account-modal__message--error">{accountActionError}</p>}
              {accountActionSuccess && <p className="account-modal__message account-modal__message--success">{accountActionSuccess}</p>}
              <div className="account-modal__actions">
                <button className="account-modal__button account-modal__button--secondary" type="button" onClick={closeAccountModal}>
                  Cancel
                </button>
                <button className="account-modal__button account-modal__button--primary" type="submit" disabled={isAccountActionLoading}>
                  {isAccountActionLoading ? 'Saving...' : 'Save Password'}
                </button>
              </div>
            </form>
          )}

          {isDeleteAccountOpen && (
            <form className="account-modal__form" onSubmit={handleDeleteAccount}>
              <p className="account-modal__warning">
                This will permanently delete your account, displays and media. This cannot be undone.
              </p>
              <label className="account-modal__field">
                <span>Type your email to confirm</span>
                <input
                  type="email"
                  value={deleteConfirmation}
                  onChange={(event) => setDeleteConfirmation(event.target.value)}
                  placeholder={accountEmail}
                  autoComplete="off"
                  required
                />
              </label>
              {accountActionError && <p className="account-modal__message account-modal__message--error">{accountActionError}</p>}
              <div className="account-modal__actions">
                <button className="account-modal__button account-modal__button--secondary" type="button" onClick={closeAccountModal}>
                  Cancel
                </button>
                <button
                  className="account-modal__button account-modal__button--danger"
                  type="submit"
                  disabled={isAccountActionLoading || deleteConfirmation !== accountEmail}
                >
                  {isAccountActionLoading ? 'Deleting...' : 'Delete Account'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    )}
    </>
  );
}; 